import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Search, Send, Star, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useAuth } from '@/contexts/AuthContext';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { QueryStateBoundary } from '@/components/ui/query-state-boundary';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { TERMS_OF_SERVICE_VERSION } from '@/constants/legal';
import { useResidentProperty } from '@/hooks/useResidentProperty';
import {
  addFavourite,
  createInvitation,
  deleteFavourite,
  getFavourites,
  recordConsent,
  updateFavourite,
} from '@/services/api.client';
import type { Favourite } from '@/types';
import { toast } from 'sonner';

// Kenyan phone: +254… or local 07…/01… (9–13 digits after stripping spaces/dashes)
const PHONE_PATTERN = /^(\+?254|0)[17]\d{7,9}$/;

// A one-tap re-invite is valid from now until the end of today, so a saved
// family member or friend can be let in the same day without filling a form.
function todayWindow(): { validFrom: string; validUntil: string } {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  return { validFrom: now.toISOString(), validUntil: end.toISOString() };
}

const schema = z.object({
  visitorName: z.string().min(2, 'Enter full name'),
  visitorPhone: z
    .string()
    .min(9, 'Enter phone number')
    .regex(PHONE_PATTERN, 'Enter a valid Kenyan phone number (e.g. +254712345678)'),
});
type FormValues = z.infer<typeof schema>;

export function FavouritesPage() {
  const qc = useQueryClient();
  const { isLoading: isAuthLoading } = useAuth();
  const prop = useResidentProperty();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [editTarget, setEditTarget] = useState<Favourite | null>(null);

  const favouritesQuery = useQuery({
    queryKey: ['favourites', prop?.propertyId, prop?.unitId],
    queryFn: () => (prop ? getFavourites(prop.propertyId, prop.unitId) : null),
    enabled: !!prop,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const addMut = useMutation({
    mutationFn: (v: FormValues) =>
      addFavourite({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        propertyId: prop!.propertyId,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        unitId: prop!.unitId,
        visitorName: v.visitorName,
        visitorPhone: v.visitorPhone,
      }),
    onMutate: async (v) => {
      const queryKey = ['favourites', prop?.propertyId, prop?.unitId];
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<Favourite[]>(queryKey);
      const tempFavourite: Favourite = {
        id: `temp-${crypto.randomUUID()}`,
        visitorName: v.visitorName,
        visitorPhone: v.visitorPhone,
        autoApprove: false,
        autoApproveTimeStart: null,
        autoApproveTimeEnd: null,
        createdAt: new Date().toISOString(),
      };
      qc.setQueryData<Favourite[]>(queryKey, (old) =>
        old ? [...old, tempFavourite] : [tempFavourite],
      );
      return { previous, queryKey };
    },
    onSuccess: () => {
      setOpen(false);
      reset();
    },
    onError: (_err, _args, context) => {
      if (context?.previous && context.queryKey) {
        qc.setQueryData(context.queryKey, context.previous);
      }
      toast.error('Action failed. Please try again.');
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['favourites'] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteFavourite(id),
    onMutate: async (id) => {
      const queryKey = ['favourites', prop?.propertyId, prop?.unitId];
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<Favourite[]>(queryKey);
      qc.setQueryData<Favourite[]>(queryKey, (old) => old?.filter((f) => f.id !== id));
      return { previous, queryKey };
    },
    onError: (_err, _args, context) => {
      if (context?.previous && context.queryKey) {
        qc.setQueryData(context.queryKey, context.previous);
      }
      toast.error('Action failed. Please try again.');
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['favourites'] });
    },
  });

  const toggleAutoApproveMut = useMutation({
    mutationFn: ({ id, autoApprove }: { id: string; autoApprove: boolean }) =>
      updateFavourite(id, { autoApprove }),
    onMutate: async ({ id, autoApprove }) => {
      const queryKey = ['favourites', prop?.propertyId, prop?.unitId];
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<Favourite[]>(queryKey);
      qc.setQueryData<Favourite[]>(queryKey, (old) =>
        old?.map((f) => (f.id === id ? { ...f, autoApprove } : f)),
      );
      return { previous, queryKey };
    },
    onError: (_err, _args, context) => {
      if (context?.previous && context.queryKey) {
        qc.setQueryData(context.queryKey, context.previous);
      }
      toast.error('Could not update auto-approval. Please try again.');
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['favourites'] });
    },
  });

  const editMut = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: { visitorName?: string; visitorPhone?: string };
    }) => updateFavourite(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['favourites'] });
      setEditTarget(null);
      toast.success('Favourite updated');
    },
    onError: () => toast.error('Could not save changes. Please try again.'),
  });

  // One-tap re-invite: creates a one-time invitation valid for the rest of
  // today so a saved family member or friend can be let in immediately. They
  // receive an SMS to register, exactly like the full invite flow.
  const reInviteMut = useMutation({
    mutationFn: (fav: Favourite) => {
      const { validFrom, validUntil } = todayWindow();
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return createInvitation(prop!.propertyId, {
        visitorName: fav.visitorName,
        visitorPhone: fav.visitorPhone,
        validFrom,
        validUntil,
      });
    },
    onSuccess: (_inv, fav) => {
      void qc.invalidateQueries({ queryKey: ['invitations', prop?.propertyId] });
      // Mirror the full invite flow's lawful-basis attestation for the audit
      // trail. Fire-and-forget — a failure here does not undo the invitation.
      void recordConsent({
        scope: 'visitor_data_lawful_basis',
        version: TERMS_OF_SERVICE_VERSION,
        granted: true,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        propertyId: prop!.propertyId,
      }).catch(() => undefined);
      toast.success(`Invited ${fav.visitorName} for today — they'll get an SMS to register.`);
    },
    onError: () => toast.error('Could not send the invitation. Please try again.'),
  });

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl">Favourites</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Save family and friends here, then tap{' '}
            <span className="font-medium text-[var(--text-primary)]">Invite</span> to let them in
            today — no forms. Turn on{' '}
            <span className="font-medium text-[var(--text-primary)]">Auto-approve</span> to let them
            walk in without your approval.
          </p>
        </div>
        <Button size="sm" onClick={() => setOpen(true)} disabled={isAuthLoading || !prop}>
          <Plus className="size-4 mr-2" />
          Add favourite
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
        <Input
          placeholder="Search by name or phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <Card>
        <CardContent className="pt-4">
          <QueryStateBoundary
            query={favouritesQuery}
            errorTitle="Couldn't load favourites"
            loadingFallback={
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="size-9 rounded-full" />
                    <div className="flex-1 space-y-1.5">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                ))}
              </div>
            }
            emptyFallback={
              <div className="py-10 text-center">
                <Star className="size-10 mx-auto mb-3 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">No favourites yet.</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Save frequent visitors to invite them faster.
                </p>
              </div>
            }
          >
            {(favs) => {
              const q = search.trim().toLowerCase();
              const filtered = q
                ? favs.filter(
                    (f) =>
                      f.visitorName.toLowerCase().includes(q) ||
                      f.visitorPhone.toLowerCase().includes(q),
                  )
                : favs;
              if (filtered.length === 0) {
                return (
                  <div className="py-10 text-center">
                    <p className="text-sm text-muted-foreground">
                      No favourites match your search.
                    </p>
                  </div>
                );
              }
              return (
                <div className="divide-y divide-border/50">
                  {filtered.map((fav) => (
                    <div key={fav.id} className="flex items-center gap-3 py-3">
                      <div className="size-9 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                        <Star className="size-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{fav.visitorName}</p>
                        <p className="text-xs text-muted-foreground">{fav.visitorPhone}</p>
                      </div>
                      <Button
                        size="sm"
                        className="shrink-0"
                        onClick={() => reInviteMut.mutate(fav)}
                        disabled={reInviteMut.isPending && reInviteMut.variables?.id === fav.id}
                        aria-label={`Invite ${fav.visitorName} for today`}
                      >
                        <Send className="size-4 sm:mr-1.5" />
                        <span className="hidden sm:inline">
                          {reInviteMut.isPending && reInviteMut.variables?.id === fav.id
                            ? 'Inviting…'
                            : 'Invite'}
                        </span>
                      </Button>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-[var(--text-tertiary)] hidden sm:inline">
                          Auto-approve
                        </span>
                        <Switch
                          checked={fav.autoApprove}
                          onCheckedChange={(checked) =>
                            toggleAutoApproveMut.mutate({ id: fav.id, autoApprove: checked })
                          }
                          disabled={toggleAutoApproveMut.isPending}
                          aria-label={`Auto-approve ${fav.visitorName}`}
                        />
                      </div>
                      <div className="flex items-center gap-1 shrink-0 sm:pl-2 sm:ml-1 sm:border-l sm:border-border/50">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8 text-muted-foreground hover:text-foreground shrink-0"
                          onClick={() => setEditTarget(fav)}
                          aria-label={`Edit ${fav.visitorName}`}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8 text-muted-foreground hover:text-destructive shrink-0"
                          onClick={() => deleteMut.mutate(fav.id)}
                          disabled={deleteMut.isPending}
                          aria-label={`Delete ${fav.visitorName}`}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              );
            }}
          </QueryStateBoundary>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Favourite Visitor</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => void handleSubmit((v) => addMut.mutate(v))(e)} noValidate>
            <FieldGroup>
              <Field>
                <FieldLabel>Full name *</FieldLabel>
                <Input
                  placeholder="Jane Kamau"
                  aria-invalid={!!errors.visitorName}
                  {...register('visitorName')}
                />
                {errors.visitorName && (
                  <p className="text-sm text-destructive">{errors.visitorName.message}</p>
                )}
              </Field>
              <Field>
                <FieldLabel>Phone *</FieldLabel>
                <Input
                  placeholder="+254 700 000 000"
                  aria-invalid={!!errors.visitorPhone}
                  {...register('visitorPhone')}
                />
                {errors.visitorPhone && (
                  <p className="text-sm text-destructive">{errors.visitorPhone.message}</p>
                )}
              </Field>
              {addMut.isError && (
                <p className="text-sm text-destructive">Something went wrong. Please try again.</p>
              )}
              <Field>
                <Button type="submit" disabled={addMut.isPending}>
                  {addMut.isPending ? 'Saving…' : 'Add favourite'}
                </Button>
              </Field>
            </FieldGroup>
          </form>
        </DialogContent>
      </Dialog>

      <EditFavouriteDialog
        target={editTarget}
        onOpenChange={(o) => {
          if (!o) setEditTarget(null);
        }}
        onSubmit={(body) => {
          if (editTarget) editMut.mutate({ id: editTarget.id, body });
        }}
        isPending={editMut.isPending}
      />
    </div>
  );
}

function EditFavouriteDialog({
  target,
  onOpenChange,
  onSubmit,
  isPending,
}: {
  target: Favourite | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (body: { visitorName: string; visitorPhone: string }) => void;
  isPending: boolean;
}) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  // Populate form fields whenever the edit target changes.
  // The previous render-time conditional (if target && name === '' && phone === '')
  // only fired on the first open — editing a second favourite without closing
  // first would show stale data from the previous edit.
  useEffect(() => {
    if (target) {
      setName(target.visitorName);
      setPhone(target.visitorPhone);
    }
  }, [target]);

  return (
    <Dialog
      open={!!target}
      onOpenChange={(next) => {
        if (!next) {
          setName('');
          setPhone('');
        }
        onOpenChange(next);
      }}
    >
      <DialogContent aria-describedby={undefined} className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Edit Favourite</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit({ visitorName: name, visitorPhone: phone });
          }}
        >
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="edit-name">Full name</FieldLabel>
              <Input
                id="edit-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="edit-phone">Phone</FieldLabel>
              <Input
                id="edit-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </Field>
            <Field>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending || !name || !phone}>
                  {isPending ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </Field>
          </FieldGroup>
        </form>
      </DialogContent>
    </Dialog>
  );
}
